import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import {
  getGiftOffers,
  createGiftOffer,
  updateGiftOffer,
  deleteGiftOffer,
  getDeliveryCompanies,
  getStoreOptions,
  getCities,
} from "../../api/adminService";

function scopeLabel(offer) {
  const companyLabel = offer.deliveryCompanyName || "All delivery companies";
  const storesLabel = offer.stores.length === 0 ? "all stores" : `${offer.stores.length} store${offer.stores.length === 1 ? "" : "s"}`;
  const citiesLabel = offer.cities.length === 0 ? "all cities" : `${offer.cities.length} cit${offer.cities.length === 1 ? "y" : "ies"}`;
  return `${companyLabel} · ${storesLabel} · ${citiesLabel}`;
}

function offerChip(offer) {
  return offer.isFree ? (
    <Chip label="Free gift" size="small" color="success" />
  ) : (
    <Chip label={`Pay ${offer.extraPaymentAmount} ₪`} size="small" color="secondary" />
  );
}

export default function GiftOffers() {
  const [offers, setOffers] = useState([]);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null); // { offer? }

  async function reload() {
    const data = await getGiftOffers();
    setOffers(data);
  }

  useEffect(() => {
    reload().catch(() => setError("Could not load gift offers. Is the API running?"));
  }, []);

  function openCreateDialog() {
    setDialog({ offer: null });
  }

  function openEditDialog(offer) {
    setDialog({ offer });
  }

  async function handleSave(payload) {
    const { offer } = dialog;
    try {
      if (offer) {
        await updateGiftOffer(offer.giftOfferId, payload);
      } else {
        await createGiftOffer(payload);
      }
      setDialog(null);
      await reload();
    } catch (err) {
      setError(err.response?.data || "Could not save this gift offer.");
    }
  }

  async function handleDelete(offer) {
    if (!window.confirm(`Delete gift offer "${offer.title}"? This cannot be undone.`)) return;
    try {
      await deleteGiftOffer(offer.giftOfferId);
      await reload();
    } catch (err) {
      setError(err.response?.data || "This gift offer could not be deleted.");
    }
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4">Gift Offers</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
          New Gift Offer
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {typeof error === "string" ? error : "Something went wrong."}
        </Alert>
      )}

      <List sx={{ bgcolor: "background.paper", borderRadius: 1 }}>
        {offers.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No gift offers yet.
          </Typography>
        )}
        {offers.map((offer, index) => (
          <React.Fragment key={offer.giftOfferId}>
            {index > 0 && <Divider component="li" />}
            <ListItem
              secondaryAction={
                <Stack direction="row" spacing={0.5}>
                  <IconButton size="small" onClick={() => openEditDialog(offer)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(offer)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              }
            >
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2">{offer.title}</Typography>
                    {offerChip(offer)}
                    <Chip label={`Worth up to ${offer.giftValueCap} ₪`} size="small" variant="outlined" />
                    {!offer.isActive && <Chip label="Inactive" size="small" color="default" />}
                  </Stack>
                }
                secondary={scopeLabel(offer)}
              />
            </ListItem>
          </React.Fragment>
        ))}
      </List>

      {dialog && <GiftOfferDialog dialog={dialog} onClose={() => setDialog(null)} onSave={handleSave} />}
    </Box>
  );
}

function GiftOfferDialog({ dialog, onClose, onSave }) {
  const { offer } = dialog;
  const [title, setTitle] = useState(offer?.title || "");
  const [description, setDescription] = useState(offer?.description || "");
  const [isFree, setIsFree] = useState(offer?.isFree ?? false);
  const [extraPaymentAmount, setExtraPaymentAmount] = useState(offer?.extraPaymentAmount ?? 50);
  const [giftValueCap, setGiftValueCap] = useState(offer?.giftValueCap ?? 300);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [storeOptions, setStoreOptions] = useState([]);
  const [selectedStores, setSelectedStores] = useState(
    offer?.stores?.map((s) => ({ storeId: s.storeId, storeName: s.storeName })) || []
  );
  const [cityOptions, setCityOptions] = useState([]);
  const [selectedCities, setSelectedCities] = useState(
    offer?.cities?.map((c) => ({ cityId: c.cityId, nameHebrew: c.name })) || []
  );
  const [isActive, setIsActive] = useState(offer?.isActive ?? true);

  useEffect(() => {
    getDeliveryCompanies()
      .then((data) => {
        setCompanyOptions(data);
        if (offer?.deliveryCompanyUserId != null) {
          const match = data.find((c) => c.userId === offer.deliveryCompanyUserId);
          if (match) setSelectedCompany(match);
        }
      })
      .catch(() => setCompanyOptions([]));
    getStoreOptions()
      .then(setStoreOptions)
      .catch(() => setStoreOptions([]));
    getCities()
      .then(setCityOptions)
      .catch(() => setCityOptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleGiftTypeChange(e, value) {
    if (value === null) return;
    setIsFree(value === "free");
  }

  const canSave =
    title.trim() && giftValueCap > 0 && (isFree || extraPaymentAmount > 0);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{offer ? "Edit Gift Offer" : "New Gift Offer"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth autoFocus />

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />

          <ToggleButtonGroup
            value={isFree ? "free" : "paid"}
            exclusive
            onChange={handleGiftTypeChange}
            size="small"
            fullWidth
          >
            <ToggleButton value="paid">Pay More, Get a Gift</ToggleButton>
            <ToggleButton value="free">Free Gift</ToggleButton>
          </ToggleButtonGroup>

          {!isFree && (
            <TextField
              label="Extra Payment (₪)"
              type="number"
              value={extraPaymentAmount}
              onChange={(e) => setExtraPaymentAmount(Number(e.target.value))}
              slotProps={{ htmlInput: { min: 0.01, step: 0.01 } }}
              fullWidth
            />
          )}

          <TextField
            label="Gift Value Up To (₪)"
            type="number"
            value={giftValueCap}
            onChange={(e) => setGiftValueCap(Number(e.target.value))}
            slotProps={{ htmlInput: { min: 0.01, step: 0.01 } }}
            fullWidth
          />

          <Autocomplete
            options={companyOptions}
            getOptionLabel={(option) => option.companyName}
            isOptionEqualToValue={(option, value) => option.userId === value.userId}
            value={selectedCompany}
            onChange={(e, value) => setSelectedCompany(value)}
            renderInput={(params) => (
              <TextField {...params} label="Delivery Company" helperText="Leave empty to apply to all delivery companies" />
            )}
          />

          <Autocomplete
            multiple
            options={storeOptions}
            getOptionLabel={(option) => option.storeName}
            isOptionEqualToValue={(option, value) => option.storeId === value.storeId}
            value={selectedStores}
            onChange={(e, value) => setSelectedStores(value)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option.storeName} size="small" {...getTagProps({ index })} key={option.storeId} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Stores" helperText="Leave empty to apply to all stores" />
            )}
          />

          <Autocomplete
            multiple
            options={cityOptions}
            getOptionLabel={(option) => option.nameHebrew}
            isOptionEqualToValue={(option, value) => option.cityId === value.cityId}
            value={selectedCities}
            onChange={(e, value) => setSelectedCities(value)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option.nameHebrew} size="small" {...getTagProps({ index })} key={option.cityId} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Cities" helperText="Leave empty to apply to all cities" />
            )}
          />

          {offer && (
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body2">Active</Typography>
              <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSave}
          onClick={() =>
            onSave({
              title: title.trim(),
              description: description.trim() || null,
              isFree,
              extraPaymentAmount: isFree ? 0 : extraPaymentAmount,
              giftValueCap,
              deliveryCompanyUserId: selectedCompany?.userId ?? null,
              storeIds: selectedStores.map((s) => s.storeId),
              cityIds: selectedCities.map((c) => c.cityId),
              isActive,
            })
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
